import React from 'react';
import { Box, Chip, ChipProps, Tooltip, useTheme } from '@mui/material';
import { CheckCircleOutline, CancelOutlined, InfoOutlined } from '@mui/icons-material';

export type StatusChipVariant = 'success' | 'error' | 'warning' | 'info' | 'default' | 'primary' | 'secondary';

interface StatusChipProps extends Omit<ChipProps, 'color'> {
  /**
   * The status variant determines the color scheme
   */
  status?: StatusChipVariant;
  /**
   * Optional tooltip text
   */
  tooltip?: string;
  /**
   * Whether to show an icon
   */
  showIcon?: boolean;
  /**
   * Custom icon to display
   */
  customIcon?: React.ReactElement;
  /**
   * Use the SyncLogs style (inline box instead of MUI Chip)
   */
  useBoxStyle?: boolean;
}

const getStatusIcon = (status: StatusChipVariant) => {
  switch (status) {
    case 'success':
      return <CheckCircleOutline />;
    case 'error':
      return <CancelOutlined />;
    case 'warning':
    case 'info':
      return <InfoOutlined />;
    default:
      return undefined;
  }
};

export const StatusChip: React.FC<StatusChipProps> = ({
  status = 'default',
  tooltip,
  showIcon = false,
  customIcon,
  useBoxStyle = false,
  label,
  ...chipProps
}) => {
  const theme = useTheme();
  const { palette } = theme;

  const getColorConfig = () => {
    switch (status) {
      case 'success':
        return {
          bgcolor: palette.success.lighter || palette.success.light + '40',
          color: palette.success.dark,
          chipColor: 'success' as const,
        };
      case 'error':
        return {
          bgcolor: palette.error.lighter || palette.error.light + '40',
          color: palette.error.dark,
          chipColor: 'error' as const,
        };
      case 'warning':
        return {
          bgcolor: palette.warning.lighter || palette.warning.light + '40',
          color: palette.warning.dark,
          chipColor: 'warning' as const,
        };
      case 'info':
        return {
          bgcolor: palette.info.lighter || palette.info.light + '40',
          color: palette.info.dark,
          chipColor: 'info' as const,
        };
      case 'primary':
        return {
          bgcolor: palette.primary.lighter || palette.primary.light + '40',
          color: palette.primary.dark,
          chipColor: 'primary' as const,
        };
      case 'secondary':
        return {
          bgcolor: palette.secondary.lighter || palette.secondary.light + '40',
          color: palette.secondary.dark,
          chipColor: 'secondary' as const,
        };
      default:
        return {
          bgcolor: palette.grey[200],
          color: palette.text.primary,
          chipColor: 'default' as const,
        };
    }
  };

  const colorConfig = getColorConfig();
  const icon = customIcon || (showIcon ? getStatusIcon(status) : undefined);

  const chipElement = useBoxStyle ? (
    <Box
      sx={{
        display: 'inline-block',
        px: 1,
        py: 0.3,
        borderRadius: '4px',
        bgcolor: colorConfig.bgcolor,
        color: colorConfig.color,
        fontSize: '11px',
        fontWeight: 600,
        ...chipProps.sx,
      }}
    >
      {label}
    </Box>
  ) : (
    <Chip label={label} icon={icon} color={colorConfig.chipColor} size="small" {...chipProps} />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{chipElement}</Tooltip>;
  }

  return chipElement;
};

export default StatusChip;
